/**
 * Utility function to seed consequence types into Firestore
 * This can be run from the browser console while authenticated as an admin
 *
 * Usage:
 * 1. Open browser console while logged in as admin
 * 2. Import and call: seedConsequenceTypes()
 */

import {
  collection,
  getDocs,
  query,
  doc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { LEGACY_CONSEQUENCE_TYPES } from '../services/disciplineConfigService.js';

/**
 * Check if consequence types already exist in Firestore
 */
export async function consequenceTypesExist(schoolId) {
  try {
    if (!schoolId) {
      throw new Error('School ID is required for hierarchical collection check');
    }
    const q = query(collection(db, `schools/${schoolId}/consequenceTypes`));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking consequence types:', error);
    throw error;
  }
}

/**
 * Seed consequence types into Firestore
 * @param {boolean} force - Force seeding even if types already exist
 * @param {string} schoolId - School ID for hierarchical collection
 * @returns {Promise<Object>} Result object with success status and count
 */
export async function seedConsequenceTypes(force = false, schoolId = null) {
  console.log('🚀 Starting consequence types seeding...\n');

  if (!schoolId) {
    // Try to get school ID from current auth context
    schoolId = prompt('Enter School ID for consequence types seeding:') || 
                 localStorage.getItem('currentSchoolId') ||
                 'demo-school'; // Fallback for testing
  }

  console.log(`📍 Targeting school: ${schoolId}\n`);

  try {
    // Check if data already exists
    const exists = await consequenceTypesExist(schoolId);
    if (exists && !force) {
      const message = `⚠️  Consequence types already exist for school ${schoolId}. Use seedConsequenceTypes(true, '${schoolId}') to force re-seed.`;
      console.warn(message);
      return {
        success: false,
        message,
        count: 0
      };
    }

    // Convert legacy types to array
    const typesToSeed = Object.values(LEGACY_CONSEQUENCE_TYPES);

    console.log(`📝 Preparing to seed ${typesToSeed.length} consequence types:\n`);
    typesToSeed.forEach(type => {
      console.log(`   - ${type.label} (${type.value})`);
    });
    console.log('');

    // Seed the data using batch write
    const batch = writeBatch(db);
    const collectionRef = collection(db, `schools/${schoolId}/consequenceTypes`);

    typesToSeed.forEach((typeData, index) => {
      const docRef = doc(collectionRef);
      batch.set(docRef, {
        ...typeData,
        sortOrder: typeData.sortOrder !== undefined ? typeData.sortOrder : index,
        active: true,
        schoolId: schoolId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    console.log('✅ Seeding completed successfully!\n');
    console.log('Summary:');
    console.log(`   - ${typesToSeed.length} consequence types added to Firestore`);
    console.log('   - Collection: consequenceTypes');
    console.log('   - All types set to active: true\n');

    // Verify the seeding
    console.log('🔍 Verifying seeding...');
    const q = query(collectionRef);
    const snapshot = await getDocs(q);
    console.log(`✓ Found ${snapshot.size} consequence types for school ${schoolId} in Firestore\n`);

    console.log('🎉 Seeding complete! You can now:');
    console.log('   1. Use the ConsequenceTypesManager UI to manage types');
    console.log('   2. Add new custom consequence types');
    console.log('   3. Edit or deactivate existing types\n');

    return {
      success: true,
      message: 'Consequence types seeded successfully',
      count: typesToSeed.length
    };
  } catch (error) {
    console.error('❌ Seeding failed:', error);
    console.error('\nError details:', error.message);
    return {
      success: false,
      message: `Seeding failed: ${error.message}`,
      count: 0,
      error
    };
  }
}

/**
 * Get current consequence types from Firestore
 */
export async function getConsequenceTypesFromFirestore() {
  try {
    const q = query(collection(db, 'consequenceTypes'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching consequence types:', error);
    throw error;
  }
}

// Make available in window for console access
if (typeof window !== 'undefined') {
  window.seedConsequenceTypes = seedConsequenceTypes;
  window.getConsequenceTypesFromFirestore = getConsequenceTypesFromFirestore;
  window.consequenceTypesExist = consequenceTypesExist;
}

export default seedConsequenceTypes;
